/**
 * ProductCardModel — the contract every card renders from.
 *
 * Named in CARD terms, never source terms. Nothing downstream of an adapter
 * may read a source-shaped field (item_code, web_item_name, formatted_price,
 * has_variants, ...) — if the renderer needs it, it belongs here first.
 *
 * Every optional slot has a safe empty default so the renderer never branches
 * on undefined. An absent value means "that slot does not render", never a crash.
 */

export const ACTION_KIND = Object.freeze({
	CART: 'cart',
	EXPLORE: 'explore',
	WHATSAPP: 'whatsapp',
	CHAT: 'chat',
	READMORE: 'readmore',
	SOLDOUT: 'soldout',
});

export const STOCK_STATE = Object.freeze({
	IN: 'in',
	OUT: 'out',
	BACKORDER: 'backorder',
	LOW: 'low',
});

export const LAYOUT = Object.freeze({
	STANDARD: 'standard',
	COMPACT: 'compact',
	MINIMAL: 'minimal',
	FEATURED: 'featured',
	PROFILE: 'profile',
});

const VALID_ACTION_KINDS = new Set(Object.values(ACTION_KIND));
const VALID_STOCK_STATES = new Set(Object.values(STOCK_STATE));

/**
 * Normalise a partial model into a complete one with safe defaults.
 * Adapters build their output through this so every model is the same shape.
 */
export function createModel(input) {
	const src = input || {};

	const media = src.media || {};
	const price = src.price || {};
	const stock = src.stock || {};
	const actions = src.actions || {};
	const state = src.state || {};
	const rating = src.rating || {};
	const countdown = src.countdown || {};
	const sizeDropdown = src.sizeDropdown || {};

	const gallery = Array.isArray(media.gallery) ? media.gallery.filter(Boolean) : [];
	if (!gallery.length && media.src) gallery.push(media.src);
	if (media.altSrc && !gallery.includes(media.altSrc)) gallery.push(media.altSrc);

	return {
		// identity — see INVARIANT 1 in validateModel()
		id: src.id || null,
		href: src.href || '#',
		itemCode: src.itemCode || null,

		media: {
			src: media.src || null,
			alt: media.alt || src.title || '',
			altSrc: media.altSrc || null,
			fallbackInitials: media.fallbackInitials || '',
			gallery,
		},

		// A person's card (profile layout): name lines, speciality, rating, price and the booking link, as the adapter shaped them. Null for a product.
		profile: src.profile && typeof src.profile === 'object' ? src.profile : null,

		badges: Array.isArray(src.badges) ? src.badges : [],
		eyebrow: src.eyebrow || '',
		title: src.title || '',
		category: src.category || '',

		price: {
			current: price.current != null ? price.current : null,
			compare: price.compare != null ? price.compare : null,
			discountPct: price.discountPct != null ? price.discountPct : null,
			currency: price.currency || '',
			isFrom: Boolean(price.isFrom),
			free: Boolean(price.free),
		},

		stock: {
			state: VALID_STOCK_STATES.has(stock.state) ? stock.state : STOCK_STATE.IN,
			label: stock.label || '',
			qty: stock.qty != null ? stock.qty : null,
		},

		swatches: Array.isArray(src.swatches) ? src.swatches : [],
		sizes: Array.isArray(src.sizes) ? src.sizes : [],
		// The size-dropdown alternate presentation (ctx.settings.sizePickerStyle
		// === 'Dropdown'): which non-colour attribute becomes a <select>, and
		// the in-stock-only options grid.js patches in once the default/manual
		// colour resolves. resolvedForColour holds the colour VALUE it was last
		// checked for (or '__none__' for a colour-less template) rather than a
		// plain boolean, so a manual re-colour can be told apart from "already done".
		sizeDropdown: {
			attribute: sizeDropdown.attribute || null,
			options: Array.isArray(sizeDropdown.options) ? sizeDropdown.options : [],
			selected: sizeDropdown.selected || null,
			resolvedForColour: sizeDropdown.resolvedForColour || null,
		},
		offers: Array.isArray(src.offers) ? src.offers : [],
		consumerDiscount: src.consumerDiscount != null ? src.consumerDiscount : null,

		rating: {
			value: rating.value != null ? rating.value : null,
			count: rating.count != null ? rating.count : null,
		},

		countdown: {
			endsAt: countdown.endsAt || null,
		},

		actions: Object.assign(
			{ kind: VALID_ACTION_KINDS.has(actions.kind) ? actions.kind : ACTION_KIND.CART },
			actions
		),

		state: {
			wished: Boolean(state.wished),
			inCart: Boolean(state.inCart),
			cartQty: Number(state.cartQty) > 0 ? Number(state.cartQty) : 0,
		},
	};
}

/**
 * Returns an array of human-readable contract violations (empty === valid).
 *
 * Called by adapters in dev; never by the renderer — the renderer's guarantee
 * is that a model which passes here renders without branching on absence.
 */
export function validateModel(model) {
	const errors = [];
	if (!model || typeof model !== 'object') return ['model is not an object'];

	// INVARIANT 1 — identity is the ROUTE, never the item code.
	//
	// Swatch selection rewrites a card's item code to the selected VARIANT
	// (e.g. JP-006-28) while the listing row is the TEMPLATE (JP-006). Keying
	// identity on the code therefore mismatches and the same product renders
	// twice on the next page of an infinite scroll. `id` is route-derived and
	// immutable; `itemCode` is separate and free to change.
	if (!model.id) {
		errors.push('id is required (route-derived; see INVARIANT 1)');
	}
	if (model.itemCode && model.id === model.itemCode) {
		errors.push('id must not be the itemCode — it must be route-derived (INVARIANT 1)');
	}

	if (!model.title) errors.push('title is required');

	// INVARIANT 2 — the cart action needs something to add to the cart.
	// An adapter that cannot supply an itemCode must not emit kind 'cart'.
	// This is what lets an editorial card (readmore, no code) and a product
	// card share one renderer without either branching on the other.
	if (model.actions.kind === ACTION_KIND.CART && !model.itemCode) {
		errors.push("actions.kind 'cart' requires itemCode (INVARIANT 2)");
	}

	if (!VALID_ACTION_KINDS.has(model.actions.kind)) {
		errors.push(`unknown actions.kind: ${model.actions.kind}`);
	}
	if (!VALID_STOCK_STATES.has(model.stock.state)) {
		errors.push(`unknown stock.state: ${model.stock.state}`);
	}

	return errors;
}

/** Throw on contract violation. For use in tests and dev builds. */
export function assertModel(model) {
	const errors = validateModel(model);
	if (errors.length) {
		throw new Error(`ProductCardModel contract violated:\n  - ${errors.join('\n  - ')}`);
	}
	return model;
}
